import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { TeamPanel } from "../../components/game/TeamPanel.js";
import type { Lobby, SelectionOptions } from "../../services/generated/client.js";
import {
  addPlaylist,
  configureParty,
  contributeLibrary,
  createTeam,
  joinTeam,
  listSelectionOptions,
  refreshSources,
  removeSource,
  setReady,
  startGame,
  type GameLink,
} from "../../services/gameLink.js";
import { spotifyStatus } from "../../services/http.js";
import { cn } from "../../lib/utils.js";
import { refusalText } from "../../lib/refusal.js";
import { SelectionPicker } from "../../components/game/SelectionPicker.js";
import { LeaveParty } from "./LeaveParty.js";
import { LobbySetup } from "./LobbySetup.js";
import { PartySources } from "./PartySources.js";

/**
 * Between joining and playing: who is here, what game, what to play from, go.
 *
 * # The screen phase 3 promised and phase 10 built
 *
 * `/lobby/:code` rendered `Placeholder` for seven phases, and the cost was not
 * cosmetic: `party.create_team` and `party.join_team` have existed since phase 3
 * and **nothing ever called them**, so `roster_to_sides` — which correctly
 * refuses a Team-mode player who is on no team — made Team mode unplayable from
 * a browser. A third of the product, behind a screen that said "coming soon".
 *
 * # It absorbed `PreGamePanel` rather than sitting beside it
 *
 * Two screens that both mean "before the game" is how they drift. The
 * picker, the device layout and the start button came from there.
 *
 * # Who sees what
 *
 * Everybody sees the roster and their own ready button. Mode, teams, layout,
 * selection and start are the host's, because the server refuses them for anybody
 * else — and offering a guest a button that always fails teaches them the game
 * is broken.
 */
export function LobbyPanel({
  link,
  lobby,
  code,
  you,
}: {
  link: GameLink;
  lobby: Lobby;
  code: string;
  /** The viewer's profile id, from the ticket. */
  you: string | null;
}) {
  const { t } = useTranslation();
  // Phase 16h. What the picker that replaced the deck one may offer — already
  // narrowed to what this installation serves (16g-2), which this screen does
  // not know and must not: the scope is enforced server-side either way.
  const [options, setOptions] = useState<SelectionOptions | null>(null);
  /**
   * Whether *this viewer* has Spotify connected — phase 19e-4.
   *
   * 🔴 **Asked here rather than carried on `Lobby`**, which is one payload
   * broadcast to everybody: whether Ana is connected is not a fact about the
   * party, and putting it there would either be wrong for everyone but one
   * person or would tell the room who has authorised what.
   *
   * Everybody asks, not just the host: contributing a library is the one source
   * a guest adds for themselves, and it is the only one that needs a connection.
   */
  const [spotify, setSpotify] = useState<{
    configured: boolean;
    connected: boolean;
    scopesCurrent: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const me = lobby.members.find((member) => member.profile_id === you) ?? null;
  const isHost = me?.role === "host";
  const players = lobby.members.filter((member) => member.role !== "display");

  useEffect(() => {
    // A guest cannot choose a selection, so asking for the list is a round trip per
    // phone in the room for nothing.
    if (!isHost) {
      return;
    }
    let cancelled = false;
    void listSelectionOptions(link).then(
      (list) => {
        if (!cancelled) {
          setOptions(list);
        }
      },
      () => {
        // Silent: the picker renders "reading the catalogue" and the party is
        // still playable with nothing selected, which is everything.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [link, isHost]);

  useEffect(() => {
    let cancelled = false;

    const ask = () => {
      void spotifyStatus().then(
        (status) => {
          if (!cancelled) {
            setSpotify({
              configured: status.configured,
              connected: status.connected,
              scopesCurrent: status.scopes_current,
            });
          }
        },
        () => {
          // Silent, and it stays `null`: the library toggle is hidden rather
          // than shown broken, and every other control in the sources panel —
          // adding a playlist, removing one — works with no Spotify account at
          // all, which is the property that makes "send a link" the free way to
          // contribute.
          //
          // 📌 Before 20f-1 this was the **normal** path for a guest, not an
          // error: `/api/spotify/status` needs a session and ADR 0007's guest
          // had no account at all, so the whole library block — including the
          // *"Connect Spotify"* line written for exactly that case — was hidden
          // from every guest in the room. The host had an account by
          // construction, which is why finding 1.1 read as "host-only". Every
          // player holds an account now, so a failure here means something is
          // actually wrong.
        },
      );
    };

    ask();

    // ⚠️ **Ask again when the tab comes back.** Connecting Spotify is a
    // navigation to a consent page and back (see `SpotifyPanel`), which from
    // inside a live lobby happens in a second tab. Without this, somebody
    // follows the panel's own advice, connects, returns — and the panel still
    // says "Connect Spotify", because nothing told it otherwise. The advice
    // would be advice that does not work.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        ask();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  /**
   * Send one lobby intent, and put the server's refusal on screen.
   *
   * Nothing here updates local state on success: every mutation acknowledges and
   * the new lobby arrives on the party topic, so the screen renders from one
   * source rather than reconciling a reply against a broadcast.
   */
  const send = (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    void action()
      .catch((cause: unknown) => {
        setError(refusalText(cause, t));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  // Why the game cannot start, in the server's own terms — checked here for the
  // button's sake and enforced there regardless. A disabled control with no
  // reason is how a host concludes the game is broken rather than unfinished.
  const blocker = startBlocker(lobby, players.length, t);

  return (
    <main className="mx-auto flex min-h-full w-full max-w-2xl flex-col gap-5 p-5 pb-16">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">{code}</p>
        <h1 className="mt-1 text-2xl font-semibold">
          {lobby.name ?? t("lobby.untitled")}
        </h1>
      </header>

      <section aria-label={t("lobby.whoIsHere")}>
        <h2 className="mb-2 text-sm font-semibold">
          {t("lobby.whoIsHere")}{" "}
          <span className="font-normal text-white/50">({players.length})</span>
        </h2>
        <ul className="flex flex-col gap-1">
          {lobby.members.map((member) => (
            <li
              key={member.profile_id}
              className="flex items-baseline justify-between gap-3 rounded-lg bg-stage-900 px-3 py-2"
            >
              <span className="min-w-0 truncate">
                {member.display_name}
                {member.profile_id === you && (
                  <span className="text-flash-400"> {t("side.yours")}</span>
                )}
                {member.role === "host" && (
                  <span className="text-white/40"> · {t("lobby.role.host")}</span>
                )}
                {member.role === "display" && (
                  <span className="text-white/40"> · {t("lobby.role.display")}</span>
                )}
              </span>
              {member.role !== "display" && (
                // In words rather than a coloured dot: this is the one screen
                // where "are we waiting for anybody" is the whole question.
                <span
                  className={cn(
                    "shrink-0 text-xs",
                    member.ready ? "text-correct-500" : "text-white/40",
                  )}
                >
                  {member.ready ? t("lobby.ready") : t("lobby.notReady")}
                </span>
              )}
            </li>
          ))}
        </ul>

        {me && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              send(() => setReady(link, !me.ready));
            }}
            className={cn(
              "mt-2 min-h-12 w-full rounded-lg border font-medium disabled:opacity-40",
              me.ready ? "border-white/20" : "border-flash-500 bg-flash-500/15",
            )}
          >
            {me.ready ? t("lobby.unready") : t("lobby.imReady")}
          </button>
        )}
      </section>

      {isHost && (
        <LobbySetup
          lobby={lobby}
          code={code}
          busy={busy}
          onMode={(mode) => {
            send(() => configureParty(link, { mode }));
          }}
          onLayout={(layout) => {
            send(() => configureParty(link, { device_layout: layout }));
          }}
          onLength={(cards) => {
            send(() => configureParty(link, { cards_to_win: cards }));
          }}
          onWildcardCap={(perTen) => {
            send(() => configureParty(link, { wildcards_per_ten: perTen }));
          }}
          onApproximateYears={(allow) => {
            send(() => configureParty(link, { allow_approximate_years: allow }));
          }}
          onPacing={(pacing) => {
            send(() => configureParty(link, { pacing }));
          }}
        />
      )}

      {lobby.mode === "team" && (
        // Shown to everybody, because **everybody moves themselves** — only
        // creating a team is the host's.
        <TeamPanel
          teams={lobby.teams}
          members={lobby.members}
          you={you}
          isHost={isHost}
          busy={busy}
          onCreate={(name) => {
            send(() => createTeam(link, name));
          }}
          onJoin={(teamId) => {
            send(() => joinTeam(link, teamId));
          }}
        />
      )}

      {/* Everybody, not just the host: adding a playlist costs no OAuth seat and
          a guest with no account can contribute one. The server owns that rule
          (`rpc/party/sources.rs`); hiding the panel here would take it away. */}
      <PartySources
        lobby={lobby}
        you={you ?? ""}
        isHost={isHost}
        spotify={spotify}
        busy={busy}
        onAddPlaylist={(pasted) => {
          send(() => addPlaylist(link, pasted));
        }}
        onContributeLibrary={(contribute) => {
          send(() => contributeLibrary(link, contribute));
        }}
        onRemoveSource={(kind, refId) => {
          send(() => removeSource(link, kind, refId));
        }}
        onRefresh={() => {
          send(() => refreshSources(link));
        }}
      />

      {isHost && (
        <SelectionPicker
          genres={options?.genres ?? null}
          decades={options?.decades ?? null}
          chosenGenres={lobby.genres}
          chosenDecades={lobby.decades}
          playableTracks={lobby.on_brief_tracks}
          wildcardTracks={lobby.wildcard_tracks}
          disabled={busy}
          onChange={(next) => {
            send(() => configureParty(link, next));
          }}
        />
      )}

      {error && (
        <p role="alert" className="text-sm text-wrong-500">
          {error}
        </p>
      )}

      {isHost ? (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={busy || blocker !== null}
            onClick={() => {
              send(() => startGame(link));
            }}
            className="min-h-14 rounded-lg bg-flash-500 px-4 text-lg font-semibold text-stage-950 disabled:opacity-40"
          >
            {busy ? t("lobby.starting") : t("lobby.start")}
          </button>
          {blocker && <p className="text-center text-xs text-coin-400">{blocker}</p>}
        </div>
      ) : (
        <p className="text-center text-sm text-white/60">{t("lobby.waitingHost")}</p>
      )}

      {/* **Phase 16f-2: the series.** A night of rematches should read as one
          night rather than as disconnected games. Every recap already lived at
          its own address; nothing had ever offered the list. */}
      {lobby.series.length > 0 && (
        <section aria-label={t("round.seriesTitle")} data-testid="party-series">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/40">
            {t("round.seriesTitle")}
          </h2>
          <ol className="flex flex-col gap-1">
            {lobby.series.map((game, index) => (
              <li key={game.game_id}>
                <a
                  href={`/recap/${game.game_id}`}
                  className="text-xs text-white/60 underline underline-offset-4 hover:text-white/90"
                >
                  {t("round.seriesGame", { n: String(index + 1) })}
                </a>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* The way out of a room somebody did not mean to be in. Phase 3 shipped
          the route and the service call and nothing ever called them, so the
          only exit was clearing a cookie. */}
      <LeaveParty isHost={isHost} />
    </main>
  );
}

/**
 * Why the game cannot start yet, or `null`.
 *
 * A **UX** copy of rules the server enforces anyway — `start` refuses each of
 * these with its own message. The point is not to prevent the call; it is that a
 * greyed-out button teaches nothing, and "everybody has to be on a team" is
 * something a host can act on in five seconds.
 *
 * Ready states are deliberately **not** in here: readiness is a signal to the
 * host, not a gate. A party waiting on one person who put their phone down is a
 * party that cannot start, and the host is the one who should decide that.
 */
function startBlocker(lobby: Lobby, playerCount: number, t: TFunction): string | null {
  // Phase 16h-2: what blocks a start is an **empty selection**, not an unchosen
  // deck. `deck_id` was still checked here after `SelectionPicker` replaced
  // `DeckPicker`, and nothing sets it any more — so every lobby refused to
  // start, saying "pick a deck" over a screen with no deck picker on it.
  //
  // Zero rather than thin, deliberately: thin is a warning `SelectionPicker`
  // already gives, and the server only actually refuses when it cannot deal
  // the starting cards. A stricter check here would disagree with the server,
  // which is the shape of blocker a host cannot act on.
  //
  // 🔴 **Both of the server's refusals, not one** — phase 19e-4. `game.start`
  // refuses an empty deck *and* a deck with no on-brief cards while the cap is
  // below ten, because the cap rations wildcards and a game with nothing to
  // spend between them draws dry on its first round. Mirroring only the first
  // would let a host press start and watch the game stop in the second it
  // began, which is the 16h-2 shape: a screen saying yes to something the
  // server then says no to.
  if (lobby.on_brief_tracks + lobby.wildcard_tracks === 0) {
    return t("lobby.blocker.noTracks");
  }
  if (lobby.on_brief_tracks === 0 && lobby.wildcards_per_ten < 10) {
    return t("lobby.blocker.onlyWildcards");
  }
  if (playerCount < 2) {
    return t("lobby.blocker.tooFew");
  }
  if (lobby.mode === "team") {
    const unassigned = lobby.members.filter(
      (member) => member.role !== "display" && !member.team_id,
    );
    if (unassigned.length > 0) {
      return t("lobby.blocker.unassigned", {
        names: unassigned.map((member) => member.display_name).join(", "),
      });
    }
    const filled = new Set(
      lobby.members
        .filter((member) => member.role !== "display")
        .map((member) => member.team_id),
    );
    if (filled.size < 2) {
      return t("lobby.blocker.tooFewTeams");
    }
  }
  return null;
}
